import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Meter, Panel } from '../common/Panel';
import { type Limit, LimitDialog } from './LimitDialog';

type Summary = RouterOutputs['period']['summary'];
type Envelope = Summary['envelopes']['items'][number];

const TONE = {
  under: 'positive',
  // biome-ignore lint/style/useNamingConvention: mirrors the API's status value
  on_track: 'positive',
  over: 'warning',
  exceeded: 'negative',
} as const;

/** Optional monthly limits over groups of categories, with this period's spending. */
export function LimitsPanel({ limits, summary }: { limits: Limit[]; summary: Summary }) {
  // undefined = closed, null = new limit
  const [editing, setEditing] = useState<Limit | null | undefined>(undefined);
  const envelopes = new Map(summary.envelopes.items.map((e) => [e.budgetId, e]));
  const { period } = summary;
  const timeShare = period.daysTotal > 0 ? period.daysElapsed / period.daysTotal : 1;

  return (
    <Panel
      title="Gränser"
      action={
        limits.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setEditing(null)}
            className="-my-1 -mr-2 text-primary hover:text-primary"
          >
            <Plus /> Lägg till
          </Button>
        )
      }
    >
      {limits.length === 0 ? (
        <div>
          <p className="text-sm text-muted-foreground">
            Vill ni hålla koll på något särskilt, t.ex. mat eller restaurang? Sätt en gräns per
            månad så ser ni hur det går.
          </p>
          <Button type="button" size="sm" className="mt-3" onClick={() => setEditing(null)}>
            <Plus /> Lägg till en gräns, t.ex. Mat
          </Button>
        </div>
      ) : (
        <>
          <ul className="-mx-2 space-y-1">
            {limits.map((limit) => (
              <LimitRow
                key={limit.id}
                limit={limit}
                envelope={envelopes.get(limit.id)}
                marker={period.isCurrent ? timeShare : undefined}
                onEdit={() => setEditing(limit)}
              />
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Gränserna är samma varje månad och varnar bara. De ändrar inte Kvar att spendera.
          </p>
        </>
      )}

      {editing !== undefined && (
        <LimitDialog
          limit={editing}
          others={limits.filter((l) => l.id !== editing?.id)}
          onClose={() => setEditing(undefined)}
        />
      )}
    </Panel>
  );
}

function LimitRow({
  limit,
  envelope,
  marker,
  onEdit,
}: {
  limit: Limit;
  envelope: Envelope | undefined;
  marker: number | undefined;
  onEdit: () => void;
}) {
  const budgeted = limit.budgetedAmount;
  const spent = envelope?.spent ?? 0;
  const over = budgeted > 0 && spent > budgeted;
  const categories = limit.categories.map((c) => c.name).join(', ');

  return (
    <li>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Ändra ${limit.name}`}
        className="block w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <span className="mb-1.5 flex items-baseline justify-between gap-3">
          <span className="truncate font-medium">{limit.name}</span>
          <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
            {kr(budgeted)}/mån
          </span>
        </span>
        {envelope && budgeted > 0 && (
          <Meter
            value={spent / budgeted}
            marker={marker}
            tone={TONE[envelope.status]}
            label={`${limit.name}: ${kr(spent)} av ${kr(budgeted)}`}
          />
        )}
        <span className="mt-1.5 flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
          <span className="min-w-0 truncate">{categories || 'Inga kategorier'}</span>
          {envelope && (
            <span className={cn('shrink-0 tabular-nums', over && 'text-negative')}>
              {over ? `${kr(spent - budgeted)} över` : `${kr(spent)} spenderat`}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}
